import { GetBetSlipData } from "../../api";
import {
  ApiUrls,
  BetSlipMode,
  BookingCodeInputTypeEnum,
  EventStatus,
  OddStatus,
  SelectionConditionEnum,
  SourcePage,
  StateActionTypes,
} from "../../types/enums";

/**
 * Dispatch a custom event
 */
export const dispatchCustomEvent = (eventName: string, detail?: any) => {
  const event = new CustomEvent(eventName, {
    detail,
    bubbles: true,
    composed: true,
  });
  document.dispatchEvent(event);
};

const BB_WIDGET_READY_TIMEOUT_MS = 5000;

let isBetBuilderWidgetReady = false;
document.addEventListener(StateActionTypes.BB_WIDGET_READY, () => {
  isBetBuilderWidgetReady = true;
});

/**
 * Resolves once the Bet Builder widget has actually mounted (BB_WIDGET_READY,
 * dispatched from BetBuilderProvider.mountWidget), or after a timeout - so a
 * permanently failed mount can't hang callers forever.
 */
export const waitForBetBuilderReady = (): Promise<void> => {
  if (isBetBuilderWidgetReady) return Promise.resolve();

  return new Promise((resolve) => {
    const timeoutId = setTimeout(() => {
      console.warn(
        "Bet Builder widget did not report ready within timeout - proceeding anyway.",
      );
      resolve();
    }, BB_WIDGET_READY_TIMEOUT_MS);

    document.addEventListener(
      StateActionTypes.BB_WIDGET_READY,
      () => {
        clearTimeout(timeoutId);
        resolve();
      },
      { once: true },
    );
  });
};

/**
 * Mirrors legacy load-bet.component.ts's addBB() retry-until-mounted guard:
 * waits for the Bet Builder widget to be mounted before adding selections
 * one by one, so Bet Builder-eligible legs aren't silently dropped by
 * BetBuilderProvider while the widget is still loading.
 */
export const loadBetSlipEvents = async (
  selections: IBetSlipSelection[],
): Promise<void> => {
  if (!selections?.length) return;
  selections.forEach((selection) => {
    dispatchCustomEvent(StateActionTypes.ADD_SELECTION, selection);
  });
};

const slugify = (value: string): string =>
  value
    .toLowerCase()
    .trim()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "");

/**
 * Builds an event page URL matching the legacy SportsBookService.routeNavigator
 * pattern: /{culture}/sportsbook/{prematch|live}/{sport}/{home}-vs-{away}-{id}
 */
export const buildEventPageUrl = (
  culture: string,
  event: { sn: string; h: string; a: string; id: number },
  isLive: boolean,
): string => {
  const type = isLive ? "live" : "prematch";
  return `/${culture}/sportsbook/${type}/${slugify(event.sn)}/${slugify(event.h)}-vs-${slugify(event.a)}-${event.id}`;
};

/**
 * Builds an event page URL directly from a Bet Builder bet - it already
 * carries sport/event data, so no fallback lookup against regular
 * selections is needed.
 */
export const buildEventUrlFromBetBuilderBet = (
  culture: string,
  bet: IBetBuilderBet,
): string => {
  const [home = "", away = ""] = bet.eventName.split(" - ");
  return buildEventPageUrl(
    culture,
    { sn: bet.sportName, h: home.trim(), a: away.trim(), id: Number(bet.eventId) },
    bet.eventStatus === EventStatus.Live,
  );
};

/**
 * Find the selection condition for bet builder logic
 */
export const findSelectionCondition = ({
  newSelection,
  betSlipState,
  betBuilderMarketIds,
  sourcePage,
}: {
  newSelection: IBetSlipSelection;
  betSlipState: IBetSlipState;
  betBuilderMarketIds: number[];
  sourcePage?: SourcePage;
}): SelectionConditionEnum => {
  const { selections, betBuilderState } = betSlipState;
  const isEventBetBuilderMarketId =
    sourcePage !== SourcePage.EVENT_LOBBY
    && sourcePage !== SourcePage.POPULAR_EVENT_CAROUSEL
    && !!newSelection.event?.bb
    && betBuilderMarketIds.indexOf(newSelection.betTypeId) !== -1;
  const isEventInSelections = selections.some(
    (selection) => selection.eventId === newSelection.eventId,
  );
  const isEventInSelectionsSameMarket =
    isEventInSelections &&
    selections.some(
      (selection) =>
        selection.eventId === newSelection.eventId &&
        selection.betTypeId === newSelection.betTypeId,
    );
  const isEventInSelectionsSameMarketOdd =
    isEventInSelections &&
    selections.some(
      (selection) =>
        selection.eventId === newSelection.eventId &&
        selection.betTypeId === newSelection.betTypeId &&
        selection.oddName === newSelection.oddName,
    );
  const isEventInBetBuilder = betBuilderState?.bets?.some(
    (bet) => Number(bet.eventId) === newSelection.eventId,
  );
  const isEventInBetBuilderSameMarket =
    isEventInBetBuilder &&
    betBuilderState?.bets?.some(
      (bet) =>
        Number(bet.eventId) === newSelection.eventId &&
        Number(bet.id) === newSelection.betTypeId,
    );
  const isEventInBetBuilderSameMarketSameOdd =
    isEventInBetBuilder &&
    betBuilderState?.bets?.some(
      (bet) =>
        Number(bet.eventId) === newSelection.eventId &&
        Number(bet.id) === newSelection.betTypeId &&
        bet.selectionName === newSelection.oddName &&
        (bet.line ?? "") === (newSelection.oddLine ?? ""),
    );

  // Both
  if (!isEventInSelections && !isEventInBetBuilder) {
    return SelectionConditionEnum.NotInNormalSelectionsNotInWidget;
  }

  // Not BB
  if (!isEventBetBuilderMarketId && isEventInBetBuilder) {
    return SelectionConditionEnum.NotBBInWidget;
  }

  if (!isEventBetBuilderMarketId && isEventInSelections) {
    return SelectionConditionEnum.NotBBInNormalSelections;
  }

  //  BB
  if (
    isEventBetBuilderMarketId &&
    isEventInSelections &&
    isEventInSelectionsSameMarket &&
    isEventInSelectionsSameMarketOdd &&
    !isEventInBetBuilder
  ) {
    return SelectionConditionEnum.isBBInNormalSelectionsSameMarketSameOddAndNotInWidget;
  }
  if (
    isEventBetBuilderMarketId &&
    isEventInSelections &&
    isEventInSelectionsSameMarket &&
    !isEventInSelectionsSameMarketOdd &&
    !isEventInBetBuilder
  ) {
    return SelectionConditionEnum.isBBInNormalSelectionsSameMarketDifferentOddAndNotInWidget;
  }
  if (
    isEventBetBuilderMarketId &&
    isEventInSelections &&
    !isEventInSelectionsSameMarket &&
    !isEventInBetBuilder
  ) {
    return SelectionConditionEnum.isBBInNormalSelectionDifferentMarketNotInWidget;
  }
  if (
    isEventBetBuilderMarketId &&
    isEventInBetBuilder &&
    isEventInBetBuilderSameMarket &&
    isEventInBetBuilderSameMarketSameOdd
  ) {
    return SelectionConditionEnum.isBBInWidgetSameMarketSameOdd;
  }
  if (
    isEventBetBuilderMarketId &&
    isEventInBetBuilder &&
    isEventInBetBuilderSameMarket &&
    !isEventInBetBuilderSameMarketSameOdd
  ) {
    return SelectionConditionEnum.isBBInWidgetSameMarketDifferentOdd;
  }

  return SelectionConditionEnum.isBBInWidgetDifferentMarket;
};

/**
 * Convert ISelectedEvent to IBetSlipSelection
 */
export const convertToBetSlipSelection = (
  selectedEvent: ISelectedEvent,
): IBetSlipSelection => {
  const { selection, event } = selectedEvent;
  const { selected, data } = selection;

  // Generate unique key similar to legacy format
  const key = `E${event.id}B${data._.id}O${selected.id}${selected.l || ""}`;

  return {
    key,
    event: event.data,
    eventId: event.id,
    eventStatus: event.data.st || 0,
    hasTCO: false, // Default value, can be set based on requirements
    homeName: event.data.h || "",
    awayName: event.data.a || "",
    gameNumber: event.gn || 0,
    gameTime: event.data.gt || "",
    score: event.data.lv
      ? `${event.data.lv.hs}-${event.data.lv.as}`
      : undefined,
    betTypeId: data._.id,
    betStatus: selected.s || 0, // Use 0 (Active) as default if status is undefined
    betTypeName: data._.n,
    oddLine: selected.l || "",
    oddPrice: parseFloat(selected.p),
    oldOddPrice: 0,
    oddKey: selected.n,
    oddName: selected.id,
    oddDisplayName: selected.n,
    isLive: event.data.st === 1, // st: 0 = Prematch, st: 1 = Live, st: 2 = Closed
    isVirtual: false, // Default value
    boostedOdd: false, // Default value
  };
};

/**
 * Returns the sourceModule shared by every bet in a Bet Builder event group,
 * or undefined if the group is empty, missing it, or the bets disagree.
 */
const getSharedSourceModule = (bets: IBetBuilderBet[]): string | undefined => {
  const [firstBet, ...rest] = bets;
  if (!firstBet?.sourceModule) return undefined;

  const allMatch = rest.every(
    (bet) => bet.sourceModule === firstBet.sourceModule,
  );
  return allMatch ? firstBet.sourceModule : undefined;
};

/**
 * Create PlaceBetSport request body from betSlipState
 */
export const createPlaceBetRequestBody = ({
  brandId,
  channelId,
  betSlipState,
  language,
  userToken,
  bookingCode = "",
  totalStake,
  bonusIds = [],
  isBooking = true,
  stakes = {},
}: {
  brandId: string;
  channelId: string;
  betSlipState: IBetSlipState;
  language: string;
  userToken: string | null;
  bookingCode?: string;
  totalStake: number;
  bonusIds?: BonusId[];
  isBooking?: boolean;
  stakes?: { [key: string]: number };
}): PlaceBetSportPayload => {
  const { selections, betBuilderState, acceptOddsChanges, mode } = betSlipState;

  // Transform regular selections
  const transformedSelections = selections.map((selection, index) => ({
    key: selection.key,
    eventId: selection.eventId,
    eventStatus: selection.eventStatus,
    homeName: selection.homeName,
    awayName: selection.awayName,
    gameTime: selection.gameTime,
    betTypeId: selection.betTypeId,
    betTypeName: selection.betTypeName,
    betStatus: selection.betStatus,
    oddPrice: selection.oddPrice, // Keep as number - API expects numeric type
    oldOddPrice: selection.oldOddPrice || null,
    oddLine: selection.oddLine || null,
    oddKey: String(selection.oddKey),
    oddName: selection.oddName,
    oddDisplayName: selection.oddDisplayName,
    isLive: selection.isLive,
    isVirtual: selection.isVirtual,
    ...(selection.score ? { score: selection.score } : {}),
    order: index + 1,
    boostedOdd: selection.boostedOdd,
    ...(selection.sourceModule && { sourceModule: selection.sourceModule }),
  }));

  // Generate selection keys for rows
  const selectionKeys = transformedSelections.map((sel) => sel.key);

  // Process Bet Builder events
  const betBuilderEvents: any[] = [];
  if (betBuilderState?.bets && betBuilderState.bets.length > 0) {
    // Group bet builder bets by eventId
    const groupedByEvent = betBuilderState.bets.reduce(
      (acc, bet) => {
        const eventId = Number(bet.eventId);
        if (!acc[eventId]) {
          acc[eventId] = [];
        }
        acc[eventId].push(bet);
        return acc;
      },
      {} as Record<number, IBetBuilderBet[]>,
    );

    // Create bet builder events
    const eventIds = Object.keys(groupedByEvent);
    eventIds.forEach((eventId: string) => {
      const bets = groupedByEvent[Number(eventId)];
      const numericEventId = Number(eventId);
      const firstBet = bets[0];
      const selectionKey = `BetBuilder${eventId}`;
      const sharedSourceModule = getSharedSourceModule(bets);

      // Find the total odds for this event
      const totalOddsEntry = betBuilderState.totalOdds?.find(
        (odd) => odd.eventId === numericEventId,
      );
      const calculatedOdds = totalOddsEntry?.odd || 0;

      // Add bet builder selection to main selections
      transformedSelections.push({
        key: selectionKey,
        eventId: numericEventId,
        eventStatus: firstBet.eventStatus || 0,
        homeName: firstBet.eventName.split(" - ")[0] || "",
        awayName: firstBet.eventName.split(" - ")[1] || "",
        gameTime: firstBet.date || "",
        betTypeId: -1,
        betTypeName: "Bet Builder",
        betStatus: 0,
        oldOddPrice: null,
        oddLine: null,
        oddPrice: calculatedOdds, // Keep as number - API expects numeric type
        oddKey: "Bet Builder",
        oddName: "Bet Builder",
        oddDisplayName: "Bet Builder",
        isLive: false,
        isVirtual: false,
        order: transformedSelections.length + 1,
        boostedOdd: false,
        ...(sharedSourceModule && { sourceModule: sharedSourceModule }),
      });

      // Add to selection keys
      selectionKeys.push(selectionKey);

      // Create bet builder event
      betBuilderEvents.push({
        EventId: numericEventId,
        SelectionKey: selectionKey,
        CalculationType: 2,
        CalculatedOdds: calculatedOdds,
        Home: firstBet.eventName.split(" - ")[0] || "",
        Away: firstBet.eventName.split(" - ")[1] || "",
        GameTime: firstBet.date || "",
        Selections: bets.map((bet: IBetBuilderBet, index: number) => ({
          BetTypeId: Number(bet.id),
          BetTypeName: bet.marketName,
          OddKey: bet.selectionName
            ? bet.selectionName.charAt(0).toUpperCase() +
              bet.selectionName.slice(1)
            : bet.selectionName,
          OddPrice: bet.odds,
          DisplayOrder: index + 1,
          OddLine: bet.line || null,
          ...(bet?.pid && { Pid: bet.pid }),
          ...(bet?.sourceModule && { sourceModule: bet.sourceModule }),
        })),
      });
    });
  }

  // Create rows based on mode
  const rows =
    mode === BetSlipMode.Single
      ? transformedSelections.map((sel) => ({
          amount: +(stakes[sel.key] ?? 0).toFixed(2),
          selectionKeys: [sel.key],
        }))
      : [{ amount: totalStake, selectionKeys: selectionKeys }];

  return {
    config: {
      brandId,
      channelId,
      language,
      userToken,
    },
    acceptOddsChanges,
    ...(bookingCode ? { bookingCode } : {}), // Only include bookingCode if not empty
    selections: transformedSelections,
    rows,
    bonusIds,
    totalStake,
    isBooking,
    BetBuilderModel: {
      BetBuilderEvents: betBuilderEvents,
    },
  };
};

/**
 * Normalize GetBetSlipDataCL response to betSlip state
 */
// LOOK IT OVER
export const normalizeBetSlipData = (
  response: GetBetSlipDataResponse,
): {
  selections: IBetSlipSelection[];
  betBuilderBets: IBetBuilderBet[];
  stakes: { [key: string]: number };
  mode: BetSlipMode;
  totalStake: number;
} => {
  if (!response.isSuccessfull || !response.data || response.data.length === 0) {
    return {
      selections: [],
      betBuilderBets: [],
      stakes: {},
      mode: BetSlipMode.Combo,
      totalStake: 0,
    };
  }

  const dataItem = response.data[0];

  // First determine mode based on structure
  let mode: BetSlipMode = BetSlipMode.Single;
  let totalStake = 0;

  if (dataItem.productData && dataItem.productData.length > 1) {
    // Multiple productData items = single mode
    mode = BetSlipMode.Single;
    totalStake = dataItem.productData.reduce(
      (sum, product) => sum + product.totalStake,
      0,
    );
  } else if (
    dataItem.productData &&
    dataItem.productData.length === 1 &&
    dataItem.productData[0].bets.length > 1
  ) {
    // One productData with multiple bets = combo mode
    mode = BetSlipMode.Combo;
    totalStake = dataItem.productData[0].totalStake;
  } else if (
    dataItem.productData &&
    dataItem.productData.length === 1 &&
    dataItem.productData[0].bets.length === 1
  ) {
    // One productData with one bet = single mode
    mode = BetSlipMode.Single;
    totalStake = dataItem.productData[0].totalStake;
  }

  // Handle bet builder events - always makes it combo mode
  if (dataItem.betBuilderEvents && dataItem.betBuilderEvents.length > 0) {
    mode = BetSlipMode.Combo;
  }

  // Convert regular bets to IBetSlipSelection
  const selections: IBetSlipSelection[] = [];
  const stakes: { [key: string]: number } = {};

  // Process all productData items
  if (dataItem.productData && dataItem.productData.length > 0) {
    dataItem.productData.forEach((product) => {
      if (product.bets) {
        product.bets.forEach((bet: GetBetSlipDataBet) => {
          const key = `E${bet.eventId}B${bet.market.id}O${bet.selection}${
            bet.market.line || ""
          }`;

          selections.push({
            key,
            event: {} as IEvent, // Will be populated when needed
            eventId: bet.eventId,
            eventStatus: bet.winStatusId,
            hasTCO: false,
            homeName: bet.homeTeam.name,
            awayName: bet.awayTeam.name,
            gameNumber: 0,
            gameTime: bet.date,
            score: undefined,
            betTypeId: bet.market.id,
            betStatus: 0,
            betTypeName: bet.market.name,
            oddLine: bet.market.line || "",
            oddPrice: bet.odd,
            oldOddPrice: 0,
            oddKey: bet.selection,
            oddName: bet.selection,
            oddDisplayName: bet.selection,
            isLive: bet.live,
            isVirtual: false,
            boostedOdd: false,
          });

          // Only store individual stakes for single mode
          if (mode === BetSlipMode.Single) {
            stakes[key] = product.stake;
          }
        });
      }
    });
  }

  // For combo mode, create single combo stake
  if (mode === BetSlipMode.Combo && selections.length > 0) {
    const selectionKeys = selections.map((s) => s.key);
    dataItem.betBuilderEvents?.forEach((bbEvent) => {
      selectionKeys.push(`BetBuilder${bbEvent.eventId}`);
    });
    const comboKey = selectionKeys.join("_");
    stakes[comboKey] = totalStake;
  }

  // Convert bet builder events to IBetBuilderBet
  const betBuilderBets: IBetBuilderBet[] = [];
  if (dataItem.betBuilderEvents) {
    dataItem.betBuilderEvents.forEach(
      (bbEvent: GetBetSlipDataBetBuilderEvent) => {
        const eventName = `${bbEvent.homeTeam} - ${bbEvent.awayTeam}`;

        bbEvent.selections.forEach(
          (selection: GetBetSlipDataBetBuilderSelection) => {
            betBuilderBets.push({
              id: String(selection.betTypeId),
              eventId: String(bbEvent.eventId),
              eventName: eventName,
              eventNameTranslation: eventName,
              marketName: selection.betTypeName,
              marketNameTranslation: selection.betTypeName,
              selectionName: selection.oddKey,
              selectionNameTranslation: selection.oddKey,
              odds: selection.oddPrice,
              // Booking-code responses carry no sport id/name for bet builder
              // events, unlike the live odds tree - see convertSelectionToBetBuilderBet
              sportName: "",
              sportId: 0,
              date: bbEvent.eventDate,
              line: selection.oddLine || undefined,
              closed: selection.oddStatus === OddStatus.Closed,
              suspended: selection.oddStatus === OddStatus.Suspended,
              previouseOdds: undefined,
              isDirty: false,
              eventClosed: bbEvent.status === EventStatus.Closed,
              eventStatus: bbEvent.status,
              lineStatus: selection.oddStatus,
              autoRemoved: false,
              addOnError: false,
            });
          },
        );
      },
    );
  }

  return {
    selections,
    betBuilderBets,
    stakes,
    mode,
    totalStake,
  };
};

/**
 * Load bet slip from booking code and dispatch events to update state
 */
// Batched fetch of live event data — hydrates booking-code selections and
// bet-builder bets alike, both of which arrive with no live IEvent data.
export const fetchEventsByIds = async (
  eventIds: (string | number)[],
): Promise<IEvent[]> => {
  if (eventIds.length === 0) return [];

  const params = eventIds.map((id) => `eventIds=${id}`).join("&");
  const response = await fetch(`${ApiUrls.GET_EVENTS}?${params}`);

  if (!response.ok) {
    throw new Error(`HTTP error! status: ${response.status}`);
  }

  const apiResponse = await response.json();
  return apiResponse.data ?? [];
};

// GetEvents' `gt` (game time) is intentionally excluded from enrichment —
// booking-code selections use `gameTime`/`date` sourced from the booking
// response itself (bet.date / bbEvent.eventDate), not the live event feed.
const withoutTimestamp = (event: IEvent): Omit<IEvent, "gt"> => {
  const rest = { ...event };
  delete (rest as Partial<IEvent>).gt;
  return rest;
};

// Merges fetched event data into selections/bet-builder bets that share an
// eventId. Items with no matching fetched event are returned unchanged.
const enrichWithEventData = <T extends { eventId: string | number }>(
  items: T[],
  eventsById: Map<number, IEvent>,
  merge: (item: T, event: IEvent) => T,
): T[] =>
  items.map((item) => {
    const event = eventsById.get(Number(item.eventId));
    return event ? merge(item, event) : item;
  });

// Fetches live event data for every eventId in this booking-code response and
// merges it into selections/BB bets. Never throws — if GetEvents fails, the
// booking code load still succeeds with the selections as normalized.
const hydrateWithEventData = async (
  rawSelections: IBetSlipSelection[],
  rawBetBuilderBets: IBetBuilderBet[],
): Promise<IBookingCodeHydrationResult> => {
  const eventIds = [
    ...new Set(
      [
        ...rawSelections.map((selection) => Number(selection.eventId)),
        ...rawBetBuilderBets.map((bet) => Number(bet.eventId)),
      ].filter((id) => Number.isFinite(id)),
    ),
  ];

  try {
    const events = await fetchEventsByIds(eventIds);
    const eventsById = new Map(events.map((event) => [event.id, event]));

    return {
      selections: enrichWithEventData(
        rawSelections,
        eventsById,
        (selection, event) => ({
          ...selection,
          event: { ...selection.event, ...withoutTimestamp(event) },
        }),
      ),
      betBuilderBets: enrichWithEventData(
        rawBetBuilderBets,
        eventsById,
        (bet, event) => ({ ...bet, sportName: event.sn, sportId: event.sid }),
      ),
    };
  } catch (error) {
    console.warn(
      "Failed to hydrate booking-code selections with live event data:",
      error,
    );
    return { selections: rawSelections, betBuilderBets: rawBetBuilderBets };
  }
};

const BARCODE_LENGTH = 15;

/**
 * Mirrors legacy load-bet.component.ts: a code is a barcode only when it is
 * exactly 15 characters long, otherwise it is treated as a booking code.
 */
const resolveBookingCodeInputType = (code: string): BookingCodeInputTypeEnum =>
  code.length === BARCODE_LENGTH
    ? BookingCodeInputTypeEnum.Barcode
    : BookingCodeInputTypeEnum.BookingCode;

export const loadBetSlipFromBookingCode = async ({
  code,
  brandId,
}: {
  code: string;
  brandId: string;
}) => {
  try {
    let bookingCode: string | undefined;
    let barcode: string | undefined;

    switch (resolveBookingCodeInputType(code)) {
      case BookingCodeInputTypeEnum.Barcode:
        barcode = code;
        break;
      case BookingCodeInputTypeEnum.BookingCode:
        bookingCode = code;
        break;
    }

    const response = await GetBetSlipData({ bookingCode, barcode, brandId });

    if (!response.isSuccessfull) {
      throw new Error("This code is expired or invalid."); // Should be error under load booking code
    }

    const {
      selections: rawSelections,
      betBuilderBets: rawBetBuilderBets,
      stakes,
      totalStake,
    } = normalizeBetSlipData(response);

    const { selections, betBuilderBets } = await hydrateWithEventData(
      rawSelections,
      rawBetBuilderBets,
    );

    selections.forEach((selection) => {
      dispatchCustomEvent(StateActionTypes.ADD_SELECTION, selection);
    });

    if (betBuilderBets.length > 0) {
      dispatchCustomEvent(StateActionTypes.ADD_BB_BETS, betBuilderBets);
    }

    const dataItem = response.data?.[0];

    const hasBetBuilderEvents = (dataItem?.betBuilderEvents?.length ?? 0) > 0;
    const hasMultipleProductData = (dataItem?.productData?.length ?? 0) > 1;
    const hasSingleProductWithMultipleBets =
      (dataItem?.productData?.length ?? 0) === 1 &&
      (dataItem?.productData?.[0]?.bets?.length ?? 0) > 1;

    let actualMode: BetSlipMode = BetSlipMode.Single;

    if (hasBetBuilderEvents || hasSingleProductWithMultipleBets) {
      actualMode = BetSlipMode.Combo;
    } else if (hasMultipleProductData) {
      actualMode = BetSlipMode.Single;
    }

    dispatchCustomEvent(StateActionTypes.SET_MODE, actualMode);

    const comboStakeFromResponse =
      dataItem?.productData?.[0]?.totalStake ??
      dataItem?.productData?.[0]?.stake ??
      0;

    if (actualMode === BetSlipMode.Single && Object.keys(stakes).length > 0) {
      Object.keys(stakes).forEach((selectionKey: string) => {
        dispatchCustomEvent(StateActionTypes.UPDATE_SINGLE_STAKE, {
          selectionKey,
          stake: stakes[selectionKey],
        });
      });
    } else if (actualMode === BetSlipMode.Combo) {
      const globalStake = totalStake || comboStakeFromResponse;

      if (globalStake > 0) {
        dispatchCustomEvent(StateActionTypes.UPDATE_GLOBAL_STAKE, globalStake);
      }
    }

    return {
      success: true,
      selections,
      betBuilderBets,
      stakes,
      mode: actualMode,
      totalStake: totalStake || comboStakeFromResponse,
    };
  } catch (error) {
    console.error("4. Error in loadBetSlipFromBookingCode:", error);
    throw error;
  }
};
